import React, { useState, useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";

import { AuthProvider, useAuth } from "./hooks/useAuth";
import { AppStateProvider, useAppState, hasLocalData, getLocalDataSummary } from "./hooks/useAppState";
import LoadingScreen from "./components/common/LoadingScreen";
import DataMigrationModal from "./components/common/DataMigrationModal";
import BottomNav from "./components/layout/BottomNav";
import Sidebar from "./components/layout/Sidebar";
import GuidedTour from "./components/onboarding/GuidedTour";
import OnboardingWizard from "./components/onboarding/OnboardingWizard";
import CarryOverModal from "./components/fiscal/CarryOverModal";
import OlliChat from "./components/ai/OlliChat";
import { calcolaAnniAttivita, getAliquotaSostitutiva } from "./utils/fiscalEngine";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import PendingApprovalPage from "./pages/PendingApprovalPage";
import { useApproval } from "./hooks/useApproval";
import RequestAccessPage from "./pages/RequestAccessPage";
import DashboardPage from "./pages/DashboardPage";
import InvoicesPage from "./pages/InvoicesPage";
import QuotesPage from "./pages/QuotesPage";
import ProfilePage from "./pages/ProfilePage";
import SubscriptionPage from "./pages/SubscriptionPage";
import FiscalSettingsPage from "./pages/FiscalSettingsPage";
import SimulazioniPage from "./pages/SimulazioniPage";
import ClientsPage from "./pages/ClientsPage";
import TimesheetPage from "./pages/TimesheetPage";
import F24Page from "./pages/F24Page";
import AdminPage from "./pages/AdminPage";
import PrivacyPage from "./pages/PrivacyPage";
import TerminiPage from "./pages/TerminiPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import CheckoutCancelPage from "./pages/CheckoutCancelPage";
import AccountantPortalPage from "./pages/AccountantPortalPage";
import AccountantSettingsPage from "./pages/AccountantSettingsPage";
import CommercialitaPage from "./pages/CommercialitaPage";

// Error Boundary to catch rendering errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Error:', error);
    console.error('[ErrorBoundary] Error Info:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-red-600 mb-2">Errore</h2>
            <p className="text-neutral-600 mb-4">Si è verificato un errore durante il caricamento.</p>
            <pre className="bg-neutral-100 p-3 rounded text-xs overflow-auto max-h-40 mb-4">
              {this.state.error?.toString()}
            </pre>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-2 bg-forfy-500 text-white rounded-lg hover:bg-forfy-600"
            >
              Ricarica pagina
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Protected Route wrapper - redirects to /accedi if not authenticated
function ProtectedRoute() {
  const { user, loading } = useAuth();
  const { loading: approvalLoading, canEnter, emailVerified } = useApproval();
  const location = useLocation();

  if (loading || approvalLoading) {
    return <LoadingScreen message="Verifica accesso..." />;
  }

  if (!user) {
    return <Navigate to="/accedi" state={{ from: location }} replace />;
  }

  // Gate degli accessi: essere autenticati non basta. Forfy è in beta privata e
  // ogni accesso è approvato a mano (approvedUsers/{email}), più la verifica
  // dell'indirizzo. Sta qui, prima di ProtectedLayout, perché è l'unico punto
  // attraversato da TUTTE le rotte autenticate — /admin compresa, che per
  // l'admin passa comunque. Le firestore.rules ripetono lo stesso controllo:
  // questo evita di mostrare l'app, quelle impediscono di scriverci.
  if (!canEnter) {
    return <PendingApprovalPage emailVerified={emailVerified} />;
  }

  // User is authenticated and approved, render the protected content
  return <ProtectedLayout />;
}

// Layout for protected routes - provides AppStateProvider and handles loading
function ProtectedLayout() {
  const { user } = useAuth();
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [migrationChecked, setMigrationChecked] = useState(false);

  useEffect(() => {
    if (user && !migrationChecked) {
      if (hasLocalData()) {
        setShowMigrationModal(true);
      }
      setMigrationChecked(true);
    }
  }, [user, migrationChecked]);

  if (!user) {
    return <LoadingScreen message="Verifica accesso..." />;
  }

  return (
    <AppStateProvider userId={user.uid}>
      <ProtectedContent />
      <MigrationHandler
        showModal={showMigrationModal}
        onClose={() => setShowMigrationModal(false)}
      />
    </AppStateProvider>
  );
}

// Carry-over di inizio anno: consolida i dati dell'anno appena chiuso.
function CarryOverHandler() {
  const {
    darkMode,
    appSettings,
    updateAppSettings,
    fiscalSettings,
    invoices,
    getFiscalDataForYear,
    setFiscalDataForYear,
  } = useAppState();

  const currentYear = new Date().getFullYear();
  const targetYear = currentYear - 1;
  const pivaYearStart = Number(fiscalSettings.pivaYearStart);
  const confirmedYear = appSettings.carryOverConfirmedYear;

  // Trigger: onboarding fatto, esiste un anno precedente reale, non ancora confermato per quest'anno.
  const shouldShow =
    appSettings.onboardingCompleted === true &&
    pivaYearStart < currentYear &&
    (confirmedYear == null || confirmedYear < currentYear);

  if (!shouldShow) return null;

  const prevData = getFiscalDataForYear(targetYear);

  // Fallback incassi: somma delle fatture incassate nell'anno target.
  const incassiFromInvoices = invoices.reduce((sum, inv) => {
    if (!inv.paid) return sum;
    const d = inv.paymentDate || inv.issueDate;
    if (!d || new Date(d).getFullYear() !== targetYear) return sum;
    return sum + (Number(inv.grossAmount) || 0);
  }, 0);

  const defaultIncassi = prevData.incassi > 0 ? prevData.incassi : incassiFromInvoices;
  const anniAttivita = calcolaAnniAttivita(pivaYearStart, targetYear);
  const aliquota = getAliquotaSostitutiva(pivaYearStart, targetYear);

  const handleConfirm = async (snapshot) => {
    await setFiscalDataForYear(targetYear, snapshot);
    await updateAppSettings({ carryOverConfirmedYear: currentYear });
  };

  const handleSkip = async () => {
    await updateAppSettings({ carryOverConfirmedYear: currentYear });
  };

  return (
    <CarryOverModal
      darkMode={darkMode}
      targetYear={targetYear}
      anniAttivita={anniAttivita}
      aliquota={aliquota}
      profitabilityCoefficient={Number(fiscalSettings.profitabilityCoefficient) || 0.78}
      defaultIncassi={defaultIncassi}
      defaultContributi={prevData.contributiInpsVersati || 0}
      defaultAcconti={prevData.accontiVersatiManuali || 0}
      incassiFromInvoices={prevData.incassi <= 0 && incassiFromInvoices > 0}
      onConfirm={handleConfirm}
      onSkip={handleSkip}
    />
  );
}

// Content inside protected layout - handles Firestore loading
function ProtectedContent() {
  const { darkMode, isLoading, syncError, clearSyncError, appSettings, updateAppSettings, fiscalSettings, setFiscalSettings, setFiscalDataForYear } = useAppState();

  // Migrazione utenti esistenti: se hanno già configurato fiscale, salta wizard
  useEffect(() => {
    if (!isLoading && appSettings.onboardingCompleted !== true && appSettings.hasConfiguredFiscal === true) {
      updateAppSettings({ onboardingCompleted: true });
    }
  }, [isLoading, appSettings.onboardingCompleted, appSettings.hasConfiguredFiscal, updateAppSettings]);

  if (isLoading) {
    return <LoadingScreen message="Caricamento dati..." darkMode={darkMode} />;
  }

  // Mostra wizard se onboarding non completato
  if (appSettings.onboardingCompleted !== true && appSettings.hasConfiguredFiscal !== true) {
    return (
      <OnboardingWizard
        darkMode={darkMode}
        fiscalSettings={fiscalSettings}
        setFiscalSettings={setFiscalSettings}
        setFiscalDataForYear={setFiscalDataForYear}
        updateAppSettings={updateAppSettings}
      />
    );
  }

  return (
    <>
      {/* Sync error toast */}
      {syncError && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-full px-4">
          <div
            className={`p-4 rounded-lg shadow-lg flex items-center justify-between ${
              darkMode ? "bg-red-900/90 text-red-100" : "bg-red-100 text-red-800"
            }`}
          >
            <span className="text-sm">{syncError}</span>
            <button
              onClick={clearSyncError}
              className={`ml-4 text-xs font-medium ${
                darkMode ? "text-red-300 hover:text-red-200" : "text-red-600 hover:text-red-700"
              }`}
            >
              Chiudi
            </button>
          </div>
        </div>
      )}

      {/* Sidebar for desktop */}
      <Sidebar darkMode={darkMode} />

      {/* Main content with bottom padding for mobile nav, left margin for desktop sidebar */}
      <div className="pb-20 md:pb-0 md:ml-56">
        <Outlet />
      </div>

      {/* Bottom nav for mobile */}
      <BottomNav darkMode={darkMode} />

      {/* Olli AI assistant */}
      <OlliChat />

      {/* Guided tour - onboarding */}
      <GuidedTour darkMode={darkMode} appSettings={appSettings} updateAppSettings={updateAppSettings} />

      {/* Carry-over inizio anno */}
      <CarryOverHandler />
    </>
  );
}

// Migration modal handler
function MigrationHandler({ showModal, onClose }) {
  const { migrateFromLocalStorage, darkMode } = useAppState();

  if (!showModal) return null;

  const localDataSummary = getLocalDataSummary();

  return (
    <DataMigrationModal
      isOpen={showModal}
      onMigrate={migrateFromLocalStorage}
      onSkip={onClose}
      localDataSummary={localDataSummary}
      darkMode={darkMode}
    />
  );
}

// Login page wrapper - redirects to dashboard if already authenticated
function LoginPageWrapper() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen message="Verifica accesso..." />;
  }

  if (user) {
    const from = location.state?.from?.pathname || "/dashboard";
    return <Navigate to={from} replace />;
  }

  return <LoginPage />;
}

// Main App component with all routes
export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/accedi" element={<LoginPageWrapper />} />
            <Route path="/richiedi-accesso" element={<RequestAccessPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/termini" element={<TerminiPage />} />
            <Route path="/commercialista/:uid/:token" element={<AccountantPortalPage />} />

            {/* Protected routes - all use the same ProtectedRoute layout */}
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/invoices" element={<InvoicesPage />} />
              <Route path="/preventivi" element={<QuotesPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/timesheet" element={<TimesheetPage />} />
              <Route path="/fiscal" element={<FiscalSettingsPage />} />
              <Route path="/f24" element={<F24Page />} />
              <Route path="/simulazioni" element={<SimulazioniPage />} />
              <Route path="/settings" element={<ProfilePage />} />
              <Route path="/commercialista-settings" element={<AccountantSettingsPage />} />
              <Route path="/commercialista" element={<CommercialitaPage />} />
              <Route path="/abbonamento" element={<SubscriptionPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
              <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
            </Route>

            {/* Legacy redirects */}
            <Route path="/profile" element={<Navigate to="/settings" replace />} />
            <Route path="/fiscal-settings" element={<Navigate to="/fiscal" replace />} />

            {/* Catch-all - redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
